import React, { Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Provider } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { BrowserRouter } from 'react-router-dom';

import { AlertProvider, ToastProvider } from 'fasoo-ui-component-next';
import _ from 'lodash';
import qs from 'query-string';
import { v4 as uuidv4 } from 'uuid';

import AppBarBiIcon from './components/fxm/logo/users/AppBarBiIcon';
import ProgressParents from './components/molecules/ProgressParents';
import useAuthStore from './hooks/auth/useAuthStore';
import useComponentWillMount from './hooks/useComponentWillMount';
import Layout from './layout/Layout';
import config from './shared/config';
import { getToken, setDeviceId } from './shared/utils/sessionStorageManager';
import { getFasooUiComponentLanguage, getLanguage } from './i18n';
import store from './store';

import './App.css';

const AppInside: React.FC = () => {
  // console.log('AppInside -- 1');
  const navigate = useNavigate();
  const { search, pathname } = useLocation();
  const { i18n } = useTranslation();
  const { isAuthenticated, isProgressSignOut, signInWithToken } = useAuthStore();
  const [token, setToken] = useState<string | null>(getToken());
  const [tokenInitialized, setTokenInitialized] = useState(isAuthenticated || !token);

  const logo = <AppBarBiIcon />;

  function tokenStorageEventHandler(): void {
    // console.log('[App.js] token storageEventHandler');
    setToken(getToken());
  }

  useEffect(() => {
    // 이전 앱에서 선택한 언어로 설정
    if (i18n.isInitialized) {
      const { lang: paramLang } = qs.parse(search);
      const language = getLanguage(Array.isArray(paramLang) ? _.head(paramLang) : paramLang);
      i18n.changeLanguage(language);
      document.getElementById('root')?.setAttribute('lang', language);
      document.documentElement.lang = language;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.isInitialized]);

  useComponentWillMount(() => {
    // 최초 1번만 실행
    // // 기동 시 uuid 하나 만들어 device_id 생성
    setDeviceId(uuidv4());
    // token 모니터링을 위한 이벤트 리스너 추가
    window.addEventListener('storage', tokenStorageEventHandler, false);
  });

  // session storage에 token이 있으면 이를 사용해 로그인 처리
  // TODO 로그인 된 상황에도 순간적으로 tokenInitialized && isAuthenticated === false인 상황이
  // 나올 수 있을 듯..
  useEffect(() => {
    async function effectAsyncFunc(): Promise<void> {
      if (!tokenInitialized && token) {
        try {
          await signInWithToken(token);
          setTokenInitialized(true);
        } catch (e) {
          // console.log('[App.js] signInWithToken catch', pathname);
          setToken(null);
          setTokenInitialized(true);
          if (pathname.indexOf('/sign-out') !== -1) {
            navigate(config.url('/sign-in'));
          } else {
            navigate(config.url('/sign-out'));
          }
        }
      }
    }
    effectAsyncFunc();
  }, [navigate, signInWithToken, token, tokenInitialized]);

  useEffect(() => {
    if (isProgressSignOut) {
      // console.log('[App.js] isProgressSignOut', pathname);
      navigate(config.url('/sign-out'));
    }
  }, [isProgressSignOut]);

  if (!tokenInitialized || !i18n.isInitialized) {
    return <div className="loading-root" />;
  }

  return (
    <AlertProvider logo={logo} lang={getFasooUiComponentLanguage() === 'en' ? 'en' : 'ko'}>
      <ToastProvider>
        <ProgressParents />
        <Layout />
      </ToastProvider>
    </AlertProvider>
  );
};

const App: React.FC = () => {
  return (
    <div>
      <Suspense fallback={<div className="loading-root" />}>
        <Provider store={store}>
          <BrowserRouter>
            <AppInside />
          </BrowserRouter>
        </Provider>
      </Suspense>
    </div>
  );
};

export default App;
